import React, { useState, useEffect, useCallback } from 'react';
import { X, DollarSign, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useStripe, Elements, PaymentElement, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { fundWallet } from '../../lib/stripe';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

interface FundWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (amount: number) => void;
  customAmount?: string;
}

const FUNDING_OPTIONS = [
  { amount: 10, label: '$10' },
  { amount: 25, label: '$25' },
  { amount: 50, label: '$50' },
  { amount: 100, label: '$100' }
];

// Initialize Stripe outside component to avoid re-initialization
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const FundWalletModalContent: React.FC<FundWalletModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showPaymentElement, setShowPaymentElement] = useState(false);

  useEffect(() => {
    // Reset state when modal opens/closes
    if (!isOpen) {
      setSelectedAmount(null);
      setCustomAmount('');
      setError(null);
      setSuccess(null);
      setClientSecret(null);
      setShowPaymentElement(false);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!stripe || !elements) {
      setError('Payment system is not ready yet. Please try again in a moment.');
      return;
    }

    if (!user) {
      setError('You must be logged in to add funds');
      return;
    }

    const amount = selectedAmount || parseFloat(customAmount);
    
    if (!amount || isNaN(amount) || amount <= 0) {
      setError('Please select a valid amount');
      return;
    }

    if (amount < 5) {
      setError('Minimum amount is $5');
      return;
    }

    if (amount > 1000) {
      setError('Maximum amount is $1,000');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!showPaymentElement) {
        // First step: Create payment intent and get client secret
        console.log(`Creating payment intent for $${amount}`);
        
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            amount,
            clientId: user.id
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create payment intent');
        }

        const data = await response.json();
        console.log('Payment intent response:', data);
        
        if (data.url) {
          // If we got a direct URL, redirect to it
          console.log('Redirecting to Stripe Checkout:', data.url);
          window.location.href = data.url;
          return;
        }
        
        if (!data.clientSecret) {
          throw new Error('No client secret received');
        }

        setClientSecret(data.clientSecret);
        setShowPaymentElement(true);
        setLoading(false);
        return;
      }

      // Second step: Confirm the payment
      console.log('Confirming payment with Elements');
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/client/dashboard?payment_success=true&amount=${amount}`,
        },
      });

      if (submitError) {
        throw submitError;
      }

      setSuccess('Payment successful! Redirecting...');
      
      // Important: We're removing the direct wallet update here
      // The wallet will be updated by the Stripe webhook instead
      if (onSuccess) {
        onSuccess(amount);
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  }, [stripe, elements, user, selectedAmount, customAmount, onSuccess, showPaymentElement]);

  if (!isOpen) return null;

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setCustomAmount(value);
      setSelectedAmount(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold">Add Funds to Wallet</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg flex items-center text-green-700">
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <p>{success}</p>
            </div>
          )}

          {!showPaymentElement ? (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-4">Select Amount</h3>
                <div className="grid grid-cols-2 gap-4">
                  {FUNDING_OPTIONS.map((option) => (
                    <button
                      type="button"
                      key={option.amount}
                      onClick={() => handleAmountSelect(option.amount)}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        selectedAmount === option.amount
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      <span className="block text-xl font-bold">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium mb-4">Or Enter Custom Amount</h3>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    placeholder="Enter amount"
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg ${
                      customAmount ? 'border-purple-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Minimum: $5, Maximum: $1,000
                </p>
              </div>
            </>
          ) : (
            <div className="mb-6">
              {clientSecret && (
                <PaymentElement />
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (!showPaymentElement && !selectedAmount && !customAmount) || !stripe}
            className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : !stripe ? (
              'Loading payment system...'
            ) : showPaymentElement ? (
              'Complete Payment'
            ) : (
              'Continue to Payment'
            )}
          </button>
        </form>

        <div className="p-4 bg-gray-50 rounded-b-lg text-center text-sm text-gray-600">
          Secure payment processing powered by Stripe
        </div>
      </div>
    </div>
  );
};

// Wrap the modal content with Stripe Elements only when the modal is open
const FundWalletModal: React.FC<FundWalletModalProps> = (props) => {
  if (!props.isOpen) return null;

  // Ensure we have a valid amount for the Stripe Elements
  const amount = props.customAmount ? parseFloat(props.customAmount) * 100 : 1000;

  const options = {
    mode: 'payment',
    amount: amount > 0 ? amount : 1000, // Ensure amount is always positive
    currency: 'usd',
    appearance: {
      theme: 'stripe',
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <FundWalletModalContent {...props} />
    </Elements>
  );
};

export default FundWalletModal;